import { getSubjectLabels } from '@/lib/settings';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, CalendarDays, Info, Loader2, MapPin, Megaphone, TrendingUp } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { durchschnitt, gleitenderSchnitt, veraenderung, vorperiodeJeTag } from '../../../lib/berichtKennlinien';
import { trialRequestReportApi, TrialRequestReport } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { useStandort } from '../../../components/layouts/use-standort';

const STANDORT_COLORS = [
  '#5aac86',
  '#4b8ec9',
  '#d9a441',
  '#b06fbb',
  '#e07a5f',
  '#3f9c9c',
  '#8892d6',
  '#c06c84',
];

const STATUS_LABELS: Record<string, string> = {
  neu: 'Neu',
  in_bearbeitung: 'In Bearbeitung',
  konvertiert: 'Konvertiert',
  ausgeblendet: 'Ausgeblendet',
  abgelehnt: 'Abgelehnt',
  weitergeleitet_legacy: 'An Altsystem weitergeleitet',
};

const toIsoDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDayLabel = (isoDate: string): string => {
  const [, month, day] = isoDate.split('-');
  return `${day}.${month}.`;
};

const formatFullDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-');
  return `${day}.${month}.${year}`;
};

type Preset = { key: string; label: string; resolve: () => { from: string; to: string } };

const PRESETS: Preset[] = [
  {
    key: '7d',
    label: 'Letzte 7 Tage',
    resolve: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 6);
      return { from: toIsoDate(from), to: toIsoDate(to) };
    },
  },
  {
    key: '30d',
    label: 'Letzte 30 Tage',
    resolve: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 29);
      return { from: toIsoDate(from), to: toIsoDate(to) };
    },
  },
  {
    key: '90d',
    label: 'Letzte 90 Tage',
    resolve: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 89);
      return { from: toIsoDate(from), to: toIsoDate(to) };
    },
  },
  {
    key: 'month',
    label: 'Dieser Monat',
    resolve: () => {
      const now = new Date();
      return {
        from: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: toIsoDate(now),
      };
    },
  },
  {
    key: 'lastMonth',
    label: 'Letzter Monat',
    resolve: () => {
      const now = new Date();
      return {
        from: toIsoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    },
  },
  {
    key: 'year',
    label: 'Dieses Jahr',
    resolve: () => {
      const now = new Date();
      return { from: toIsoDate(new Date(now.getFullYear(), 0, 1)), to: toIsoDate(now) };
    },
  },
];

const Reports = () => {
  const { standorte } = useStandort();
  const defaultRange = useMemo(() => PRESETS[1].resolve(), []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [standortId, setStandortId] = useState<number | null>(null);
  // T-0159: Herkunft, Diagrammart und Kennlinien.
  const [channel, setChannel] = useState<string>('');
  const [diagramm, setDiagramm] = useState<'balken' | 'linie'>('balken');
  const [kennlinien, setKennlinien] = useState({ schnitt: true, gleitend: false, vorperiode: false });
  const [report, setReport] = useState<TrialRequestReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await trialRequestReportApi.get(from, to, standortId, channel || null);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bericht konnte nicht geladen werden');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [from, to, standortId, channel]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const applyPreset = (preset: Preset) => {
    const range = preset.resolve();
    setFrom(range.from);
    setTo(range.to);
  };

  // Der Bericht liefert die Standorte selbst mit, damit auch Anfragen aus
  // Standorten sichtbar bleiben, die nicht im Kontext des Users liegen.
  const standortOptions = useMemo(() => {
    if (report?.standorte?.length) {
      return report.standorte.map((entry) => ({ id: entry.standortId, name: entry.standortName }));
    }
    return standorte.map((entry) => ({ id: entry.id, name: entry.name }));
  }, [report, standorte]);

  const standortNameById = useMemo(() => {
    const map = new Map<number, string>();
    standortOptions.forEach((entry) => map.set(entry.id, entry.name));
    (report?.byStandort || []).forEach((row) => map.set(row.standortId, row.standortName));
    return map;
  }, [standortOptions, report]);

  // Beim Blick auf alle Standorte werden die Tagesbalken nach Standort gestapelt,
  // bei einem einzelnen Standort genuegt ein Balken.
  const stackedStandortIds = useMemo(() => {
    if (standortId || !report) return [];
    return report.byStandort.filter((row) => row.total > 0).map((row) => row.standortId);
  }, [report, standortId]);

  const tageswerte = useMemo(() => (report?.days ?? []).map((day) => day.total), [report]);
  const schnitt = useMemo(() => durchschnitt(tageswerte), [tageswerte]);
  const chartData = useMemo(() => {
    if (!report) return [];
    const gleitend = gleitenderSchnitt(tageswerte);
    const vorher = vorperiodeJeTag(report.days.length, report.previousDays ?? []);
    return report.days.map((day, index) => {
      const point: Record<string, string | number | null> = {
        date: day.date,
        label: formatDayLabel(day.date),
        total: day.total,
        gleitend: gleitend[index],
        vorperiode: vorher[index],
      };
      stackedStandortIds.forEach((sid) => {
        point[`s${sid}`] = day.byStandort[String(sid)] || 0;
      });
      return point;
    });
  }, [report, stackedStandortIds, tageswerte]);

  const tickInterval = useMemo(() => {
    const count = chartData.length;
    if (count <= 14) return 0;
    return Math.floor(count / 14);
  }, [chartData.length]);

  const maxStandortTotal = useMemo(
    () => Math.max(1, ...(report?.byStandort || []).map((row) => row.total)),
    [report],
  );
  const maxChannelTotal = useMemo(
    () => Math.max(1, ...(report?.byChannel || []).map((row) => row.total)),
    [report],
  );

  const percentOf = (value: number, total: number): string => {
    if (!total) return '0 %';
    return `${Math.round((value / total) * 100)} %`;
  };

  const vorperiodeHinweis = (jetzt: number, vorher?: number): string => {
    if (vorher === undefined) return '';
    const delta = veraenderung(jetzt, vorher);
    return `Vorperiode: ${vorher}${delta === null ? '' : ` (${delta > 0 ? '+' : ''}${delta} %)`}`;
  };

  const formatEuro = (cents: number): string =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
      (cents || 0) / 100,
    );

  const renderTooltip = ({ active, payload, label }: {
    active?: boolean;
    label?: string | number;
    payload?: Array<{ name?: string; value?: number; color?: string; dataKey?: string | number }>;
  }) => {
    if (!active || !payload?.length) return null;
    const point = chartData.find((entry) => entry.label === label);
    const rows = payload.filter((entry) => Number(entry.value) > 0);
    return (
      <div className="rounded-md border bg-background p-3 text-xs shadow-md">
        <p className="mb-1 font-medium">
          {point ? formatFullDate(String(point.date)) : String(label ?? '')}
        </p>
        {rows.length === 0 ? (
          <p className="text-muted-foreground">Keine Anfragen</p>
        ) : (
          rows.map((entry) => (
            <p key={String(entry.dataKey)} className="flex items-center gap-2">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
              <span className="font-medium">{Number(entry.value).toLocaleString('de-DE')}</span>
            </p>
          ))
        )}
      </div>
    );
  };

  const totals = report?.totals;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Berichte</h1>
        <p className="text-muted-foreground">
          Probestunden-Anfragen über alle Standorte, aufgeschlüsselt pro Tag
        </p>
      </div>

      {/* Filter */}
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3 xl:grid-cols-5">
          <div>
            <label htmlFor="report-from" className="mb-1 block text-sm font-medium">
              Von
            </label>
            <input
              id="report-from"
              type="date"
              value={from}
              max={to}
              onChange={(event) => setFrom(event.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="report-to" className="mb-1 block text-sm font-medium">
              Bis
            </label>
            <input
              id="report-to"
              type="date"
              value={to}
              min={from}
              onChange={(event) => setTo(event.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="report-standort" className="mb-1 block text-sm font-medium">
              Standort
            </label>
            <select
              id="report-standort"
              value={standortId === null ? '' : String(standortId)}
              onChange={(event) =>
                setStandortId(event.target.value ? Number(event.target.value) : null)
              }
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="">Alle Standorte</option>
              {standortOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="report-herkunft" className="mb-1 block text-sm font-medium">
              Herkunft
            </label>
            <select
              id="report-herkunft"
              value={channel}
              onChange={(event) => setChannel(event.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="">Alle Herkünfte</option>
              {(report?.channels ?? []).map((option) => (
                <option key={option.channel} value={option.channel}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={loadReport} disabled={isLoading} className="w-full">
              {isLoading ? 'Lade…' : 'Aktualisieren'}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 border-t p-4">
          {PRESETS.map((preset) => {
            const range = preset.resolve();
            const isActive = range.from === from && range.to === to;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => applyPreset(preset)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading && !report && (
        <div className="flex items-center justify-center rounded-lg border bg-card p-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {report && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: 'Anfragen gesamt',
                value: String(totals?.total ?? 0),
                icon: <CalendarDays size={20} />,
                hint: vorperiodeHinweis(totals?.total ?? 0, report.previousTotal),
              },
              {
                title: 'Ø pro Tag',
                value: new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(
                  totals?.averagePerDay ?? 0,
                ),
                icon: <TrendingUp size={20} />,
                hint: `${report.range.days} Tage im Zeitraum`,
              },
              {
                title: 'Stärkster Tag',
                value: String(totals?.peakCount ?? 0),
                icon: <BarChart3 size={20} />,
                hint: totals?.peakDate ? formatFullDate(totals.peakDate) : 'Keine Anfragen',
              },
              {
                title: 'Davon über Ads',
                value: String(totals?.paid ?? 0),
                icon: <Megaphone size={20} />,
                hint: `${percentOf(totals?.paid ?? 0, totals?.total ?? 0)} aller Anfragen`,
              },
            ].map((stat) => (
              <div key={stat.title} className="rounded-lg border bg-card p-4 shadow-sm">
                <div className="rounded-md bg-primary/10 p-2 text-primary w-fit">{stat.icon}</div>
                <div className="mt-3">
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <h3 className="text-2xl font-bold">{stat.value}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{stat.hint}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tagesverlauf */}
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b p-4">
              <div>
                <h2 className="font-semibold">Anfragen pro Tag</h2>
                <p className="text-sm text-muted-foreground">
                  {standortId
                    ? standortNameById.get(standortId) || 'Ausgewählter Standort'
                    : `Alle Standorte, ${diagramm === 'balken' ? 'gestapelt' : 'je Linie'} nach Standort`}
                  {channel ? ` · ${report.channels?.find((c) => c.channel === channel)?.label ?? channel}` : ''} ·
                  Zeitzone {report.range.timezone}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Diagrammart">
                  {(['balken', 'linie'] as const).map((art) => (
                    <button
                      key={art}
                      type="button"
                      aria-pressed={diagramm === art}
                      onClick={() => setDiagramm(art)}
                      className={`rounded px-3 py-1 text-xs ${
                        diagramm === art ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {art === 'balken' ? 'Balken' : 'Linie'}
                    </button>
                  ))}
                </div>
                {(
                  [
                    ['schnitt', 'Durchschnitt'],
                    ['gleitend', '7-Tage-Schnitt'],
                    ['vorperiode', 'Vorperiode'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="flex cursor-pointer items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={kennlinien[key]}
                      onChange={(event) => setKennlinien((prev) => ({ ...prev, [key]: event.target.checked }))}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4">
              {totals?.total === 0 ? (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  Im gewählten Zeitraum gibt es keine Probestunden-Anfragen.
                </p>
              ) : (
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                      <XAxis
                        dataKey="label"
                        interval={tickInterval}
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} />
                      <Tooltip content={renderTooltip} cursor={{ opacity: 0.1 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {stackedStandortIds.length > 0
                        ? stackedStandortIds.map((sid, index) =>
                            diagramm === 'balken' ? (
                              <Bar
                                key={sid}
                                dataKey={`s${sid}`}
                                name={standortNameById.get(sid) || `Standort ${sid}`}
                                stackId="standorte"
                                fill={STANDORT_COLORS[index % STANDORT_COLORS.length]}
                                radius={index === stackedStandortIds.length - 1 ? [3, 3, 0, 0] : undefined}
                              />
                            ) : (
                              <Line
                                key={sid}
                                type="monotone"
                                dataKey={`s${sid}`}
                                name={standortNameById.get(sid) || `Standort ${sid}`}
                                stroke={STANDORT_COLORS[index % STANDORT_COLORS.length]}
                                strokeWidth={2}
                                dot={false}
                              />
                            ),
                          )
                        : diagramm === 'balken' ? (
                          <Bar dataKey="total" name="Anfragen" fill={STANDORT_COLORS[0]} radius={[3, 3, 0, 0]} />
                        ) : (
                          <Line type="monotone" dataKey="total" name="Anfragen" stroke={STANDORT_COLORS[0]} strokeWidth={2} dot={false} />
                        )}
                      {kennlinien.gleitend ? (
                        <Line
                          type="monotone"
                          dataKey="gleitend"
                          name="7-Tage-Schnitt"
                          stroke="#4b5563"
                          strokeWidth={2}
                          dot={false}
                          connectNulls={false}
                        />
                      ) : null}
                      {kennlinien.vorperiode ? (
                        <Line
                          type="monotone"
                          dataKey="vorperiode"
                          name="Vorperiode"
                          stroke="#9ca3af"
                          strokeDasharray="5 4"
                          strokeWidth={1.5}
                          dot={false}
                        />
                      ) : null}
                      {kennlinien.schnitt ? (
                        <ReferenceLine
                          y={schnitt}
                          stroke="#e07a5f"
                          strokeDasharray="4 3"
                          label={{ value: `Ø ${schnitt.toLocaleString('de-DE')}`, position: 'insideTopRight', fontSize: 11 }}
                        />
                      ) : null}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Funnel */}
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="border-b p-4">
              <h2 className="font-semibold">Von der Anfrage zum bezahlten Kurs</h2>
              <p className="text-sm text-muted-foreground">
                Verfolgt die Anfragen aus dem gewählten Zeitraum weiter — unabhängig davon, wann
                Probestunde oder Zahlung stattfanden.
              </p>
            </div>
            <div className="space-y-4 p-4">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {report.funnel.stages.map((stage, index) => {
                  const previous = index > 0 ? report.funnel.stages[index - 1] : null;
                  const first = report.funnel.stages[0];
                  return (
                    <div key={stage.key} className="rounded-md border p-3">
                      <p className="text-xs text-muted-foreground">{stage.label}</p>
                      <p className="text-2xl font-bold">{stage.total}</p>
                      {previous ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {percentOf(stage.total, previous.total)} von {previous.label} ·{' '}
                          {percentOf(stage.total, first.total)} gesamt
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-muted-foreground">Ausgangsmenge</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {report.funnel.stages[1]?.total === 0 ? (
                <p className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
                  Aus diesem Zeitraum wurde noch keine Anfrage in eine Probestunde umgewandelt. Der
                  Funnel füllt sich, sobald Anfragen im Office konvertiert werden.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 font-medium">Herkunft</th>
                        <th className="px-4 py-2 text-right font-medium">Anfragen</th>
                        <th className="px-4 py-2 text-right font-medium">Probestunden</th>
                        <th className="px-4 py-2 text-right font-medium">Anmeldungen</th>
                        <th className="px-4 py-2 text-right font-medium">Bezahlt</th>
                        <th className="px-4 py-2 text-right font-medium">Abschlussquote</th>
                        <th className="px-4 py-2 text-right font-medium">Umsatz</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.funnel.byChannel.map((row) => (
                        <tr key={row.channel} className="border-t">
                          <td className="px-4 py-2">
                            <span className="flex items-center gap-2">
                              {row.label}
                              {row.paid && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                                  bezahlt
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">{row.requests}</td>
                          <td className="px-4 py-2 text-right">{row.trials}</td>
                          <td className="px-4 py-2 text-right">{row.enrollments}</td>
                          <td className="px-4 py-2 text-right">{row.paidCount}</td>
                          <td className="px-4 py-2 text-right">
                            {percentOf(row.paidCount, row.requests)}
                          </td>
                          <td className="px-4 py-2 text-right font-medium">
                            {formatEuro(row.revenueCents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted/20 font-medium">
                        <td className="px-4 py-2">Gesamt</td>
                        <td className="px-4 py-2 text-right">{report.funnel.stages[0]?.total ?? 0}</td>
                        <td className="px-4 py-2 text-right">{report.funnel.stages[1]?.total ?? 0}</td>
                        <td className="px-4 py-2 text-right">{report.funnel.stages[2]?.total ?? 0}</td>
                        <td className="px-4 py-2 text-right">{report.funnel.stages[3]?.total ?? 0}</td>
                        <td className="px-4 py-2 text-right">
                          {percentOf(
                            report.funnel.stages[3]?.total ?? 0,
                            report.funnel.stages[0]?.total ?? 0,
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {formatEuro(report.funnel.revenueCents)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Junge Zeiträume sehen zwangsläufig schlechter aus: eine Anfrage von gestern kann
                noch keinen Vertrag haben. Für belastbare Abschlussquoten einen Zeitraum wählen, der
                weit genug zurückliegt.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Nach Standort */}
            <div className="rounded-lg border bg-card shadow-sm">
              <div className="flex items-center gap-2 border-b p-4">
                <MapPin size={16} className="text-muted-foreground" />
                <h2 className="font-semibold">Nach Standort</h2>
              </div>
              <div className="p-4">
                {report.byStandort.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Keine Anfragen im Zeitraum.</p>
                ) : (
                  <ul className="space-y-3">
                    {report.byStandort.map((row) => (
                      <li key={row.standortId}>
                        <div className="flex items-baseline justify-between text-sm">
                          <span className="font-medium">{row.standortName}</span>
                          <span className="text-muted-foreground">
                            {row.total} · {percentOf(row.total, totals?.total ?? 0)}
                          </span>
                        </div>
                        <div className="mt-1 h-2 w-full rounded-full bg-muted">
                          <div
                            className="h-2 rounded-full bg-primary"
                            style={{ width: `${(row.total / maxStandortTotal) * 100}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Nach Kanal */}
            <div className="rounded-lg border bg-card shadow-sm">
              <div className="flex items-center gap-2 border-b p-4">
                <Megaphone size={16} className="text-muted-foreground" />
                <h2 className="font-semibold">Herkunft (organisch vs. Ads)</h2>
              </div>
              <div className="p-4">
                {report.byChannel.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Keine Anfragen im Zeitraum.</p>
                ) : (
                  <ul className="space-y-3">
                    {report.byChannel.map((row) => (
                      <li key={row.channel}>
                        <div className="flex items-baseline justify-between text-sm">
                          <span className="flex items-center gap-2 font-medium">
                            {row.label}
                            {row.paid && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                                bezahlt
                              </span>
                            )}
                          </span>
                          <span className="text-muted-foreground">
                            {row.total} · {percentOf(row.total, totals?.total ?? 0)}
                          </span>
                        </div>
                        <div className="mt-1 h-2 w-full rounded-full bg-muted">
                          <div
                            className={`h-2 rounded-full ${row.paid ? 'bg-amber-500' : 'bg-primary'}`}
                            style={{ width: `${(row.total / maxChannelTotal) * 100}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Nach Fach (T-0159) */}
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b p-4">
              <BarChart3 size={16} className="text-muted-foreground" />
              <h2 className="font-semibold">Nach {getSubjectLabels().singular}</h2>
            </div>
            <div className="p-4">
              {(report.byFach ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Anfragen im Zeitraum.</p>
              ) : (
                <ul className="grid gap-x-8 gap-y-3 md:grid-cols-2">
                  {(report.byFach ?? []).map((row) => (
                    <li key={row.fachId ?? 'ohne'}>
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="font-medium">{row.fachName}</span>
                        <span className="text-muted-foreground">
                          {row.total} · {percentOf(row.total, totals?.total ?? 0)}
                        </span>
                      </div>
                      <div className="mt-1 h-2 w-full rounded-full bg-muted">
                        <div
                          className="h-2 rounded-full bg-primary"
                          style={{ width: `${(row.total / Math.max(1, report.byFach?.[0]?.total ?? 1)) * 100}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Bearbeitungsstand */}
          {report.byStatus.length > 0 && (
            <div className="rounded-lg border bg-card shadow-sm">
              <div className="border-b p-4">
                <h2 className="font-semibold">Bearbeitungsstand</h2>
                <p className="text-sm text-muted-foreground">
                  Anfragen von Standorten, die noch ans Altsystem weiterleiten, werden dort
                  bearbeitet und erscheinen hier nur als Kopie.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 p-4">
                {report.byStatus.map((row) => (
                  <div key={row.status} className="rounded-md border px-4 py-2">
                    <p className="text-xs text-muted-foreground">
                      {STATUS_LABELS[row.status] || row.status}
                    </p>
                    <p className="text-lg font-semibold">
                      {row.total}{' '}
                      <span className="text-xs font-normal text-muted-foreground">
                        {percentOf(row.total, totals?.total ?? 0)}
                      </span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Kampagnen */}
          {report.byCampaign.length > 0 && (
            <div className="rounded-lg border bg-card shadow-sm">
              <div className="border-b p-4">
                <h2 className="font-semibold">Kampagnen (UTM)</h2>
                <p className="text-sm text-muted-foreground">
                  Top 25 nach Anzahl der Anfragen — mit dem Weg bis zur Bezahlung
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 font-medium">Quelle</th>
                      <th className="px-4 py-2 font-medium">Medium</th>
                      <th className="px-4 py-2 font-medium">Kampagne</th>
                      <th className="px-4 py-2 text-right font-medium">Anfragen</th>
                      <th className="px-4 py-2 text-right font-medium">Probestunden</th>
                      <th className="px-4 py-2 text-right font-medium">Anmeldungen</th>
                      <th className="px-4 py-2 text-right font-medium">Bezahlt</th>
                      <th className="px-4 py-2 text-right font-medium">Umsatz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byCampaign.map((row) => (
                      <tr
                        key={`${row.utmSource}|${row.utmMedium}|${row.utmCampaign}`}
                        className="border-t"
                      >
                        <td className="px-4 py-2">{row.utmSource}</td>
                        <td className="px-4 py-2">{row.utmMedium}</td>
                        <td className="px-4 py-2">{row.utmCampaign}</td>
                        <td className="px-4 py-2 text-right font-medium">{row.total}</td>
                        <td className="px-4 py-2 text-right">{row.trials ?? 0}</td>
                        <td className="px-4 py-2 text-right">{row.enrollments ?? 0}</td>
                        <td className="px-4 py-2 text-right">{row.paidCount ?? 0}</td>
                        <td className="px-4 py-2 text-right">{formatEuro(row.revenueCents ?? 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3 rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
            <Info size={16} className="mt-0.5 shrink-0" />
            <p>
              Die Herkunft wird aus den mitgesendeten UTM-Parametern und den Click-IDs von Google
              und Meta bestimmt. Diese werden nur erfasst, wenn die Besucherin oder der Besucher im
              Cookie-Banner der Marketing-Kategorie zugestimmt hat — ohne Einwilligung erscheint die
              Anfrage unter „Direkt / unbekannt“ ({totals?.unattributed ?? 0} von{' '}
              {totals?.total ?? 0} im Zeitraum).
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
